import {createSlice, type PayloadAction} from '@reduxjs/toolkit';

export type QuickstartState = {
  hasResume: boolean;
  hasJob: boolean;
  hasTailored: boolean;
  hasExported: boolean;
};

const defaultState: QuickstartState = {
  hasResume: false,
  hasJob: false,
  hasTailored: false,
  hasExported: false,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isQuickstartState(object: unknown): object is QuickstartState {
  if (!isRecord(object)) return false;

  // `object` is now narrowed to Record<string, unknown>, so these member accesses are safe
  return (
    typeof object.hasResume === 'boolean' &&
    typeof object.hasJob === 'boolean' &&
    typeof object.hasTailored === 'boolean' &&
    typeof object.hasExported === 'boolean'
  );
}

const localKey = 'qs_state';

function loadInitial(): QuickstartState {
  try {
    const raw = localStorage.getItem(localKey);
    if (!raw) return defaultState;

    const parsed: unknown = JSON.parse(raw);
    if (isQuickstartState(parsed)) return parsed;
    return defaultState;
  } catch {
    return defaultState;
  }
}

const initialState: QuickstartState = loadInitial();

const slice = createSlice({
  name: 'quickstart',
  initialState,
  reducers: {
    setHasResume(state, action: PayloadAction<boolean>) {
      state.hasResume = action.payload;
    },
    setHasJob(state, action: PayloadAction<boolean>) {
      state.hasJob = action.payload;
    },
    setHasTailored(state, action: PayloadAction<boolean>) {
      state.hasTailored = action.payload;
    },
    setHasExported(state, action: PayloadAction<boolean>) {
      state.hasExported = action.payload;
    },
    reset(state) {
      state.hasResume = false;
      state.hasJob = false;
      state.hasTailored = false;
      state.hasExported = false;
    },
  },
});

export const {setHasResume, setHasJob, setHasTailored, setHasExported, reset} =
  slice.actions;
export default slice.reducer;

export function persistQuickstartState(state: QuickstartState) {
  try {
    localStorage.setItem(localKey, JSON.stringify(state));
  } catch {
    // ignore persistence errors
  }
}
